/* Copyright (c) 2025 Xperi. All Rights Reserved. */

/**
 * This module defines the `InfocardActions` class and its methods for handling various
 * actions related to info cards in the application.
 * It imports necessary kernels, types, and interactors to manage actions for offers,
 * unified collections, VOD content, and recordings.
 *
 * Key functionalities include:
 * - Retrieving actions for offers, unified collections, VOD content, and recordings.
 * - Validating required identifiers such as `offerId`, `contentId`, `recordingId`, and
 * `unifiedCollectionId` before fetching actions.
 * - Interacting with the `ServicesClientImpl` to fetch action lists based on the provided
 * parameters.
 * - Supporting different types of preview kernels such as `OfferPreviewKernel`,
 * `ContentPreviewKernel`, `RecordingPreviewKernel`, and `UnifiedCollectionPreviewKernel`.
 *
 * The module integrates with various services to provide a unified interface for managing
 * actions related to info cards.
 */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import {
    ActionGroup,
    ActionList,
    ContentInfoCardKernel,
    InfoCardKernel,
    OfferInfoCardKernel,
    PreviewKernel,
    RecordingInfoCardKernel,
    UnifiedCollectionInfoCardKernel,
    VodOfferPreviewKernel,
} from 'client-code/actions';
import { ScreenType } from 'client-code/previews';
import { ContentVodItemKernel, UnifiedCollectionVodItemKernel } from 'client-code/vod-browse';

import { ServicesClientImpl } from '../interactors';
import { TPreviewKernel } from '../types';

export class InfocardActions {
    private readonly TAG: string = 'InfoCardActions';

    private mInfoCardKernel:
        | InfoCardKernel
        | PreviewKernel
        | TPreviewKernel
        | ContentVodItemKernel
        | UnifiedCollectionVodItemKernel;

    private mServicesInteractor: ServicesClientImpl;

    public constructor(infoCardKernel: InfoCardKernel) {
        this.mInfoCardKernel = infoCardKernel;
        this.mServicesInteractor = new ServicesClientImpl();
    }

    /**
     * Retrieves the actions for an offer.
     *
     * @returns A promise that resolves to the list of actions for the offer.
     * @throws {Error} Throws an error if the `offerId` is invalid or not provided.
     */
    public async getOfferActionsInfoCard(): Promise<ActionList> {
        const { offerId } = this.mInfoCardKernel as OfferInfoCardKernel;
        if (!offerId) {
            throw new Error('Invalid offerId.');
        }
        const params = {
            groups: [
                ActionGroup.Watch,
                ActionGroup.ScheduleBookmark,
                ActionGroup.ScheduleOnepass,
                ActionGroup.Rent,
                ActionGroup.ScheduleRecording,
            ],
            offerId,
            screenType: ScreenType.All,
        };
        try {
            const { actionList } = await this.mServicesInteractor.getOfferActionsV2(params);
            return actionList;
        } catch (error) {
            Diagnostics.error(this.TAG, 'Offer Actions', error);
            throw new Error('Failed to fetch offer actions');
        }
    }

    /**
     * Retrieves the unified collection actions for the info card.
     *
     * @returns A promise that resolves to an object containing the unified collection actions.
     * @throws Error If `unifiedCollectionId` is not available in `mInfoCardKernel`.
     */
    public async getUnifiedCollectionActionsInfoCard(): Promise<ActionList> {
        const { unifiedCollectionId, partnerId, contentSupplierPartnerId, collapsedVodItemId } =
            this.mInfoCardKernel as UnifiedCollectionInfoCardKernel;
        if (!unifiedCollectionId) {
            throw new Error('Invalid unifiedCollectionId.');
        }
        const params = {
            groups: [ActionGroup.Watch, ActionGroup.ScheduleOnepass],
            unifiedCollectionId,
            partnerId,
            contentSupplierPartnerId,
            collapsedVodItemId,
            screenType: ScreenType.All,
        };
        try {
            const { actionList } =
                await this.mServicesInteractor.getUnifiedCollectionActions(params);
            return actionList;
        } catch (error) {
            Diagnostics.error(this.TAG, 'Unified Collection Actions', error);
            throw new Error('Failed to fetch unified collection actions');
        }
    }

    /**
     * Retrieves the list of actions for a VOD (Video On Demand) offer or content.
     *
     * @returns A promise that resolves to the list of actions for the VOD offer or content.
     * @throws Error If `offerId` or `contentId` is not available in `mInfoCardKernel`.
     */
    public async getVodActionsInfoCard(): Promise<ActionList> {
        const { offerId } = this.mInfoCardKernel as VodOfferPreviewKernel;
        const { contentId } = this.mInfoCardKernel as ContentInfoCardKernel;
        if (offerId) {
            const params = {
                vodOfferId: offerId,
                groups: [
                    ActionGroup.Watch,
                    ActionGroup.ScheduleBookmark,
                    ActionGroup.ScheduleOnepass,
                ],
                screenType: ScreenType.All,
            };
            try {
                const { actionList } = await this.mServicesInteractor.getVodOfferActionsV2(params);
                return actionList;
            } catch (error) {
                Diagnostics.error(this.TAG, 'VOD Offer Actions', error);
                throw new Error('Failed to fetch VOD offer actions');
            }
        }
        if (contentId) {
            return this.getContentActionsInfoCard();
        }
        throw new Error('Invalid offerId or contentId.');
    }

    /**
     * Retrieves the list of actions available for a specific content item.
     *
     * @returns A promise that resolves to the list of actions for the content.
     * @throws Throws an error if the content ID is not available.
     */
    public async getContentActionsInfoCard(): Promise<ActionList> {
        const { contentId, partnerId, contentSupplierPartnerId, collapsedVodItemId } = this
            .mInfoCardKernel as ContentInfoCardKernel;
        if (!contentId) {
            throw new Error('Invalid contentId.');
        }
        const params = {
            contentId,
            partnerId,
            contentSupplierPartnerId,
            collapsedVodItemId,
            groups: [
                ActionGroup.Watch,
                ActionGroup.ScheduleBookmark,
                ActionGroup.ScheduleOnepass,
                ActionGroup.Rent,
                ActionGroup.ScheduleRecording,
            ],
            screenType: ScreenType.All,
        };
        try {
            const { actionList } = await this.mServicesInteractor.getContentActions(params);
            return actionList;
        } catch (error) {
            Diagnostics.error(this.TAG, 'Content Actions', error);
            throw new Error('Failed to fetch content actions');
        }
    }

    /**
     * Retrieves the list of actions available for a specific recording.
     *
     * @returns A promise that resolves to the list of actions for the recording.
     * @throws Throws an error if the recording ID is not available.
     */
    public async getRecordingActionsInfoCard(): Promise<ActionList> {
        const { recordingId } = this.mInfoCardKernel as RecordingInfoCardKernel;
        if (!recordingId) {
            throw new Error('Invalid recordingId.');
        }
        const params = {
            recordingId,
            groups: [
                ActionGroup.Watch,
                ActionGroup.ScheduleBookmark,
                ActionGroup.ScheduleOnepass,
                ActionGroup.ScheduleRecording,
            ],
            screenType: ScreenType.All,
        };
        try {
            const { actionList } = await this.mServicesInteractor.getRecordingActionsV2(params);
            return actionList;
        } catch (error) {
            Diagnostics.error(this.TAG, 'Recording Actions', error);
            throw new Error('Failed to fetch recording actions');
        }
    }
}
